import React, { useState, useEffect, useContext } from 'react'; // Added useState, useEffect
import { SubscriptionContext } from '../SubscriptionContext';
import './SubscriptionManagement.css'; // Import CSS for styling

const SubscriptionManagement = () => {
  // Destructure createCustomerPortalSession and refreshSubscription from context
  const { userSubscription, createCheckoutSession, createCustomerPortalSession  } = useContext(SubscriptionContext);

  const [portalLoading, setPortalLoading] = useState(false); // State for loading portal
  const [portalError, setPortalError] = useState(null);     // State for portal-specific errors
  const [referralCode, setReferralCode] = useState('');     // State for referral code input
  const [showReferralInput, setShowReferralInput] = useState(false); // State to show/hide referral input

  // Effect to ensure subscription data is loaded when the component mounts
  // or if userSubscription is null (e.g., after a refresh or initial load)
  useEffect(() => {
      if (!userSubscription) {
      }
  }, [userSubscription]); // Depend on userSubscription and refreshSubscription

  if (!userSubscription) {
    return (
      <div className="subscription-card loading-card">
        <p>Loading subscription details...</p>
      </div>
    );
  }

  // Destructure all necessary fields from userSubscription
  const {
    subscription_status,
    subscription_tier,
    question_limit,
    free_questions_used, // Added this for potential display
    stripe_customer_id,   // Crucial for managing in Stripe
    current_period_end    // Crucial for displaying renewal date
  } = userSubscription;

  const subscriptionStatus = subscription_status;
  const subscriptionTier = subscription_tier;
  const freeQuestionsLimit = question_limit;

  // Helper to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Stripe's currentPeriodEnd is a Unix timestamp (seconds), Date expects milliseconds
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  // Convert subscriptionTier to lowercase for consistent comparison
  const currentTierLowercase = subscriptionTier ? subscriptionTier.toLowerCase() : 'free';

  // Handler for the "Manage in Stripe Customer Portal" button
  const handleManageInStripe = async () => {
    setPortalLoading(true);
    setPortalError(null); // Clear previous errors
    try {
      await createCustomerPortalSession(); // This function will handle the redirect
    } catch (err) {
      console.error("Error managing subscription in Stripe:", err);
      setPortalError("Failed to open subscription management portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  // Determine if the "Manage in Stripe" button should be shown
  // Show if there's a Stripe Customer ID AND they are not on the 'free' or 'beta' tier.
  const showManageInStripeButton = 
    stripe_customer_id && 
    currentTierLowercase !== 'free' && 
    currentTierLowercase !== 'beta';

  return (
    <div className="subscription-card">
      <h3 className="card-title">Your Current Subscription</h3>

      <div className="current-status">
        <p><strong>Status:</strong> <span className={`status-badge ${subscriptionStatus}`}>{subscriptionStatus.replace('_', ' ').toUpperCase()}</span></p>
        <p><strong>Tier:</strong> <span className="tier-name">{subscriptionTier}</span></p>

        {/* Display Questions Used/Limit for Free Tier */}
        {currentTierLowercase === 'free' && (
          <p><strong>Questions Used:</strong> {free_questions_used || 0} / {freeQuestionsLimit || 0}</p>
        )}

        {/* Display Renewal/Trial End Date */}
        {(subscriptionStatus === 'active' || subscriptionStatus === 'trialing') && current_period_end && (
          <p><strong>{subscriptionStatus === 'trialing' ? 'Trial Ends' : 'Renews'}:</strong> {formatDate(current_period_end)}</p>
        )}

        {/* Action Required Message for Canceled/Past Due */}
        {(subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') && (
          <p className="action-required-message">Your subscription is {subscriptionStatus}. Please upgrade or reactivate to regain full access.</p>
        )}

        {/* Beta Tester Note */}
        {currentTierLowercase === 'beta' && (
            <p className="beta-note">You are currently a Beta Tester. You may upgrade to a standard account if you like to gain access to our more advanced features.</p>
        )}
      </div>

      {portalError && <p className="error-message">{portalError}</p>} {/* Display portal-specific errors */}

      {/* NEW: Section for managing subscription via Stripe Portal */}
      {showManageInStripeButton && (
        <div className="manage-portal-section">
          <button
            onClick={handleManageInStripe}
            disabled={portalLoading}
            className="manage-portal-button" // Apply new CSS class
          >
            {portalLoading ? "Opening Portal..." : "Manage Subscription & Payment"}
          </button>
          <p className="manage-portal-info">
            You will be redirected to Stripe's secure customer portal to update payment methods,
            change plans, or cancel your subscription.
          </p>
        </div>
      )}

      {/* Only show upgrade options if currently on 'free' tier */}
      {(currentTierLowercase === 'free' || subscriptionStatus === 'canceled' || currentTierLowercase === 'beta') && (
        <>
          <h4 className="upgrade-title">Upgrade Your Plan</h4>
          <div className="plan-options">
            {/* Standard Plan */}
            <div className="plan-card">
              <h4>Standard Plan</h4>
              <p className="plan-price">
                <span className="original-price">$14.99</span>
                $4.99<span>/month</span>
                <span className="discount-tag">(67% off!)</span>
              </p>
              <ul className="plan-features">
                <li>✓ Analytics to help you improve</li>
                <li>✓ AI generated session reviews</li>
                <li>✓ Unlimited questions</li>
              </ul>
              <div className="referral-section">
                <button
                  onClick={() => setShowReferralInput(!showReferralInput)}
                  className="referral-toggle-button"
                >
                  {showReferralInput ? 'Hide' : 'Have a referral code?'}
                </button>
                
                {showReferralInput && (
                  <div className="referral-input-section">
                    <input
                      type="text"
                      placeholder="Enter referral code"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      className="referral-input"
                    />
                    <p className="referral-info">
                      Everyone gets the referral code ROCKETPREP25 now!
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={() => createCheckoutSession('standard', 'ROCKETPREP25')}
                className="upgrade-button basic"
                disabled={
                  (currentTierLowercase === 'standard' && subscriptionStatus != 'canceled')||
                  currentTierLowercase === 'premium' ||
                  subscriptionStatus === 'trialing'
                }
              >
                {
                currentTierLowercase === 'standard' ? 'Current Plan' :
                 currentTierLowercase === 'premium' ? 'Already Premium' :
                 currentTierLowercase === 'beta' ? 'Upgrade from Beta to Standard' : // Show specific text for beta
                 subscriptionStatus === 'trialing' ? 'Trialing...' :
                 subscriptionStatus === 'canceled' ? 'Canceled...' : 'Choose Standard'}

              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionManagement;
